import { type IReturn, nameOf } from "@servicestack/client";
import { useCallback, useContext, useMemo } from "react";
import useSWR, { type SWRConfiguration, type SWRResponse } from "swr";

import ServiceStackClientContext from "src/contexts/api/ServiceStackClientContext";

/**
 * Lists HTTP methods supported by the shared ServiceStack/SWR adapter.
 */
export type IHttpVerb = "get" | "post" | "put" | "delete";

/**
 * Connects typed ServiceStack requests to SWR using request identity and serialized content.
 *
 * Postconditions: An absent request produces a null key and no fetch request. Active keys include
 * verb, request name and JSON payload.
 *
 * Assumptions: Requests are JSON-serializable and the context client supports the verb; request
 * construction/serialization failures propagate.
 */
const useSwrApi = <T>(
    httpVerb: IHttpVerb,
    fn?: () => IReturn<T>,
    configuration?: SWRConfiguration
): SWRResponse<T, T> => {
    const client = useContext(ServiceStackClientContext);

    const request = useMemo(() => fn?.() ?? null, [fn]);

    const key = useMemo(
        () =>
            request ? `SwrClient:${httpVerb}:${nameOf(request)}:${JSON.stringify(request)}` : null,
        [request, httpVerb]
    );

    const fetcher = useCallback(
        () => (request ? client[httpVerb](request) : null),
        [client, request, httpVerb]
    );

    return useSWR(key, fetcher, configuration) as SWRResponse<T, T>;
};

export default useSwrApi;
